import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { flushSync } from 'react-dom';
import { useData as useDataVike } from 'vike-react/useData';

import { useEffectOnce } from '@/renderer/utils/hooks/useEffectOnce';
import { useRefUpdating } from '@/renderer/utils/hooks/useRefUpdating';
import { useSsr } from '@/renderer/utils/hooks/useSSr';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import { authToken } from '@/renderer/utils/useMe';
import { RasContextStore } from '@/renderer/utils/useRasContext';

type Expired = { expired: true };

function isExpired<T>(data: T | { expired: true }): data is Expired {
  return (data as Expired)?.expired === true;
}

function rasContextReducer(
  state: RasContextData,
  {
    action,
    data,
  }:
    | { action: 'RESET'; data: RasContextData }
    | { action: 'user'; data: { user: API.User | null; authToken?: string | null | undefined } }
    | { action: 'SET'; data: Partial<Omit<RasContextData, 'user'>> },
) {
  // @todo this way of setting authToken here sucks, find a better way?
  if (action === 'user') {
    if (!state) {
      state = {};
    }

    if (data?.authToken || data?.authToken === null) {
      state.authToken = data.authToken;

      if (data.authToken) {
        if (authToken() !== data.authToken) {
          authToken(data.authToken);
        }
      } else if (authToken()) {
        authToken(null);
      }
    }

    if (data?.user || data?.user === null) {
      const handler: ProxyHandler<RasContextData> = {
        get(target, prop) {
          if (prop === 'user') {
            return data.user;
          }

          return Reflect.get(target, prop);
        },
      };

      return new Proxy(state, handler);
    }
  } else if (action === 'RESET') {
    if (data?.authToken) {
      if (authToken() !== data.authToken) {
        authToken(data.authToken);
      }
    } else if (data?.authToken === null && authToken()) {
      authToken(null);
    } else if (data?.authToken === undefined && data?.user === null) {
      authToken(null);
    }

    return data;
  } else if (action === 'SET' && data) {
    let changed = 0;

    if (!state) {
      state = {};
      changed = -1;
    }

    for (const k in data) {
      const key = k as keyof Omit<RasContextData, 'user'>;
      // @todo why did we get a 'seasons' error here?
      if (state[key] !== data[key]) {
        state[key] = data[key];
        changed = changed === 0 ? 1 : changed;
      }
    }

    if (data?.authToken || data?.authToken === null) {
      if (data.authToken) {
        if (authToken() !== data.authToken) {
          authToken(data.authToken);
        }
      } else if (authToken()) {
        authToken(null);
      }
    }

    if (changed === 1) {
      return { ...state };
    }
  }

  return state;
}

export function RasContextProvider({ children }: { children: React.ReactNode }) {
  const data = useDataVike<RasContextData>();
  const [currentContext, dispatch] = useReducer(rasContextReducer, data);

  const { isBrowser } = useSsr();
  const isBrowserRef = useRefUpdating(isBrowser);
  const userRef = useRefUpdating(currentContext?.data?.user);

  useUpdateEffect(() => {
    if (data === undefined) {
      if (userRef.current === undefined && isBrowserRef.current) {
        // We don't have a user, so let's fetch it
      }
    } else if (data && (!data.userPromise || Object.keys(data).length !== 1)) {
      // in SSR, data is always an object. in CSR, we want to ignore this.
      if (data.user !== undefined && userRef.current === undefined) {
        // reset if we are adding new user state
        dispatch({ action: 'RESET', data });
      } else {
        // or just set if we don't have new user data to change
        dispatch({ action: 'SET', data });
      }
    }
  }, [data, data?.user]);

  useEffect(() => {
    const theData = data;
    (async () => {
      if (theData?.userPromise) {
        const { userPromise, ...otherData } = theData;

        const userData = await userPromise;

        if (!isExpired(userData?.data) && userData?.data?.data) {
          flushSync(() =>
            dispatch({ action: 'RESET', data: { ...otherData, user: userData.data.data } }),
          );
        }
      }
    })();
  }, [data, data?.userPromise]);

  const setUser = useCallback(
    (user: API.User | null, authTokenValue?: string | null | undefined) => {
      flushSync(() => dispatch({ action: 'user', data: { user, authToken: authTokenValue } }));
      Sentry.setUser({ id: user?.id });
    },
    [],
  );

  const setData = useCallback<NonNullable<RasContext['setData']>>(
    (key, value) => dispatch({ action: 'SET', data: { [key]: value } }),
    [],
  );

  useEffectOnce(() => {
    Sentry.setUser({ id: data?.user?.id });
  });

  const context = useMemo<RasContext>(
    () => ({
      data: currentContext,
      setData,
      setUser,
    }),
    [currentContext, setData, setUser],
  );

  return <RasContextStore.Provider value={context}>{children}</RasContextStore.Provider>;
}
